import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { formatDateTime } from '../../../lib/format';
import type {
  CursorPage, Plant, ReportDefinitionRow, ReportJobRow, ReportJobStatus,
} from '@dru/types';
import {
  Alert, Button, Card, CardBody, CardHeader, DataTable, IconAction, InfoTip, PermissionGate, RowActions,
  SearchSelect, SelectField, StatusBadge, Tabs, TextField, TextSkeleton, type Tone, cn,
} from '@dru/ui';
import { Download, FileSpreadsheet } from 'lucide-react';
import { api } from '../../../lib/api';
import { useAuth, errorMessage } from '../../../lib/auth';
import { useQuery } from '../../../lib/useQuery';
import { PageHeader, holds } from '../../../layouts/ConsoleShell';
import { LabReportsRegister } from '../components/CompanyProfileReports';

/**
 * Exports.
 *
 * Nothing is generated while you wait. A report over a company's workforce
 * reads tens of thousands of rows, so asking for one queues a job and the file
 * arrives shortly — which means this screen has to be honest about waiting
 * rather than pretending a spinner is a download.
 *
 * The catalogue comes from the server and already excludes reports this user
 * could not run: a role that cannot open the certificate register is not
 * offered its export. That is a permission check, not a rendering decision —
 * the endpoint refuses it too.
 *
 * An export contains only what the person asking could already see. A
 * plant-bound user's roster covers their plants, because the scope they held
 * when they asked is frozen onto the job and re-bound when it runs.
 */

const statusTone = (s: ReportJobStatus): Tone =>
  s === 'READY' ? 'covered'
  : s === 'FAILED' ? 'overdue'
  : s === 'EXPIRED' ? 'neutral'
  : 'pending';

const STATUS_LABEL: Record<ReportJobStatus, string> = {
  PENDING: 'Queued', RUNNING: 'Generating', READY: 'Ready',
  FAILED: 'Failed', EXPIRED: 'Expired',
};

const size = (bytes: number | null) => {
  if (bytes === null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

/** Reports whose result depends on a date range. */
const RANGED = new Set([
  'APPOINTMENT_SCHEDULE',
  'EXAMINATION_STATUS',
  'FORM_32_HEALTH_REGISTER',
  'FORM_35_DISEASE_NOTICE',
  'ANNUAL_HEALTH_RETURN',
]);
/** Reports where "how far ahead" is the question. */
const HORIZONED = new Set(['CERTIFICATE_EXPIRY']);

/**
 * What comes out of each export, in the reader's words, and how sensitive it
 * is. Mirrors the column lists in the API's report-definitions.ts — the
 * catalogue endpoint returns only a label and a sentence, and "what exactly
 * leaves the building in this file" is the question somebody forwarding a
 * spreadsheet should be able to answer before they ask for it. A report the
 * server adds before this map knows about it simply shows no line.
 */
type Sensitivity = 'personal' | 'fitness' | 'aggregate' | 'operational';
const CONTENTS: Record<string, { columns: string; sensitivity: Sensitivity }> = {
  EMPLOYEE_ROSTER: { columns: 'Employee ID, name, plant and its code, employment type, gender, date of birth, mobile, email, blood group, status, joining and exit dates — plus older details still on file (job role, contract, shift).', sensitivity: 'personal' },
  CONTRACTOR_WORKFORCE: { columns: 'Contractor, licence and expiry, employee name and ID, plant, job role, contract dates, status.', sensitivity: 'operational' },
  EXAMINATION_STATUS: { columns: 'Employee, plant, campaign, examination type, hospital, status, raised, due and SLA dates.', sensitivity: 'operational' },
  CERTIFICATE_EXPIRY: { columns: 'Employee, plant, certificate number, fitness outcome, issue and expiry dates, days remaining.', sensitivity: 'fitness' },
  APPOINTMENT_SCHEDULE: { columns: 'Date and time, employee, plant, examination, venue, attendance and cancellation reason.', sensitivity: 'operational' },
  RESTRICTIONS: { columns: 'Employee, plant, job role, work area, shift, restriction code and its public label, validity.', sensitivity: 'fitness' },
  COMPLIANCE_BY_PLANT: { columns: 'Per plant: workforce, covered, coverage %, due, overdue, never examined, in progress.', sensitivity: 'aggregate' },
  FORM_32_HEALTH_REGISTER: { columns: 'Per employee: sex, age, hazards, examination date, fitness outcome, statutory restrictions, certifying surgeon.', sensitivity: 'fitness' },
  FORM_35_DISEASE_NOTICE: { columns: 'Per notified employee: plant and address, sex, age, exposure, fitness decision, restrictions applied, certifying surgeon.', sensitivity: 'fitness' },
  ANNUAL_HEALTH_RETURN: { columns: 'Per plant: workforce, employees in hazardous operations, examined, counts by fitness outcome, coverage %.', sensitivity: 'aggregate' },
};
const SENSITIVITY: Record<Sensitivity, { label: string; tone: Tone }> = {
  personal: { label: 'Personal contact details', tone: 'warning' },
  fitness: { label: 'Fitness outcomes', tone: 'warning' },
  aggregate: { label: 'Counts only', tone: 'neutral' },
  operational: { label: 'Operational', tone: 'neutral' },
};

/**
 * Reports: the workers' health check reports, and the CSV exports.
 *
 * Two tabs, each shown only to a role that may use it. The tab is in the
 * address (`?tab=`), so a link to the exports opens on the exports.
 */
export function Reports() {
  const { permissions } = useAuth();
  const canHealth = holds(permissions, 'report.health-read');
  // The exports list is read with report.read; a role without it is not
  // offered a tab that would only answer with a refusal.
  const canExports = holds(permissions, 'report.read');
  const [params, setParams] = useSearchParams();
  const tab = canHealth && (params.get('tab') !== 'exports' || !canExports) ? 'health' : 'exports';
  const setTab = (k: string) => setParams((p) => {
    const n = new URLSearchParams(p);
    if (k === 'exports') n.set('tab', 'exports'); else n.delete('tab');
    return n;
  }, { replace: true });

  return (
    <div className="space-y-5">
      <PageHeader
        title={canHealth && canExports ? 'Reports' : canHealth ? 'Health reports' : 'Exports'}
        hint={canHealth
          ? 'Employees\' health check reports, as your company\'s report policy allows, and spreadsheet exports of your workforce and registers.'
          : 'Spreadsheets of your workforce, examinations and statutory registers. Generated in the background and kept for 24 hours.'}
      />
      {canHealth && canExports && (
        <Tabs
          tabs={[{ key: 'health', label: 'Health reports' }, { key: 'exports', label: 'Exports' }]}
          active={tab}
          onChange={setTab}
        />
      )}
      {tab === 'health' ? <LabReportsRegister /> : <Exports />}
    </div>
  );
}

function Exports() {
  const { permissions } = useAuth();
  const canExport = holds(permissions, 'report.export');
  const [reportType, setReportType] = useState('');
  const [plantId, setPlantId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [withinDays, setWithinDays] = useState('90');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  const catalogue = useQuery<{ items: ReportDefinitionRow[] }>(
    () => api.get<{ items: ReportDefinitionRow[] }>('/reports'), []);
  const plants = useQuery<CursorPage<Plant>>(
    () => api.get<CursorPage<Plant>>('/plants?limit=100'), []);
  const jobs = useQuery<{ items: ReportJobRow[] }>(
    () => api.get<{ items: ReportJobRow[] }>('/reports/jobs?limit=25'), []);

  const definitions = catalogue.data?.items ?? [];
  const selected = definitions.find((d) => d.key === reportType);
  const rows = jobs.data?.items ?? [];

  const pending = useMemo(
    () => rows.some((j) => j.status === 'PENDING' || j.status === 'RUNNING'),
    [rows],
  );

  /**
   * Polls only while something is actually generating, and stops when it is
   * done. A dashboard that polls for ever is a dashboard that keeps a laptop
   * awake and a database busy for no reason.
   */
  const reload = useRef(jobs.reload);
  reload.current = jobs.reload;
  useEffect(() => {
    if (!pending) return undefined;
    const t = setInterval(() => reload.current(), 3000);
    return () => clearInterval(t);
  }, [pending]);

  const request = async () => {
    if (!reportType) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      const params: Record<string, unknown> = {};
      if (plantId) params.plantId = plantId;
      if (RANGED.has(reportType)) {
        if (from) params.from = from;
        if (to) params.to = to;
      }
      if (HORIZONED.has(reportType)) params.expiringWithinDays = Number(withinDays);

      await api.post('/reports/export', { reportType, parameters: params },
                     crypto.randomUUID());
      setNotice('Queued. It will appear below as soon as it is generated.');
      jobs.reload();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const download = async (id: string) => {
    setDownloading(id); setError(null);
    try {
      const grant = await api.get<{ url: string }>(`/reports/jobs/${id}/download`);
      window.open(grant.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="space-y-5">
      <Alert tone="info" title="What an export never contains">
        No test values, findings, diagnoses or physician notes — in any export, for any role.{' '}
        <InfoTip label="More about what an export contains">
          Some registers carry the fitness outcome and statutory restrictions, because the Factories
          Act requires them; those are marked below. Each export covers only the plants you can see,
          and every request and download is recorded in the audit trail.
        </InfoTip>
      </Alert>

      {error && <Alert tone="danger">{error}</Alert>}
      {notice && <Alert tone="success">{notice}</Alert>}

      <Card>
        <CardHeader
          title={canExport ? 'New export' : 'Available exports'}
          hint={canExport
            ? 'Choose what you need. The file contains only what you can already see on screen.'
            : 'Your role can see what exists but not generate files. Ask a Company Admin to run an export for you.'}
        />
        <CardBody className="space-y-4">
          {catalogue.loading && <TextSkeleton lines={4} />}
          {catalogue.error && <Alert>{catalogue.error}</Alert>}
          {!catalogue.loading && !catalogue.error && definitions.length === 0 && (
            <p className="text-small text-neutral-600">No export is open to your role.</p>
          )}
          {definitions.length > 0 && (
            <fieldset>
              <legend className="sr-only">Report</legend>
              <ul className="grid gap-2 lg:grid-cols-2">
                {definitions.map((d) => {
                  const info = CONTENTS[d.key];
                  const chosen = d.key === reportType;
                  // On a phone a card is its name and its sensitivity until
                  // it is chosen; ten cards with their descriptions and
                  // column lists were 2,300px before the Generate button.
                  // Choosing one opens it. A role that cannot choose sees
                  // everything, as before.
                  const fold = canExport && !chosen && 'max-md:hidden';
                  const body = (
                    <>
                      <span className="block text-body font-medium text-neutral-900">{d.label}</span>
                      <span className={cn('mt-0.5 block text-small text-neutral-600', fold)}>{d.description}</span>
                      {info && (
                        <span className="mt-1.5 block text-caption text-neutral-500">
                          <StatusBadge tone={SENSITIVITY[info.sensitivity].tone} className="mr-1.5 align-middle">
                            {SENSITIVITY[info.sensitivity].label}
                          </StatusBadge>
                          <span className={cn(fold)}>{info.columns}</span>
                        </span>
                      )}
                    </>
                  );
                  return (
                    <li key={d.key}>
                      {canExport ? (
                        <label
                          className={cn(
                            'flex h-full cursor-pointer gap-3 rounded-input border p-3 transition-colors',
                            chosen ? 'border-primary-500 bg-primary-50' : 'border-neutral-200 hover:bg-neutral-50',
                          )}
                        >
                          <input
                            type="radio"
                            name="report"
                            value={d.key}
                            checked={chosen}
                            onChange={() => setReportType(d.key)}
                            className="mt-1 h-4 w-4 shrink-0 border-neutral-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="min-w-0">{body}</span>
                        </label>
                      ) : (
                        <div className="h-full rounded-input border border-neutral-200 p-3">{body}</div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </fieldset>
          )}

          {canExport && reportType && (
            <div className="grid gap-4 border-t border-neutral-100 pt-4 sm:grid-cols-3">
              <SearchSelect label="Plant" value={plantId} onChange={setPlantId}
                placeholder="All plants I can see" searchPlaceholder="Search plants by name or code…"
                options={[
                  { value: '', label: 'All plants I can see' },
                  ...(plants.data?.items ?? []).map((p) => ({ value: p.id, label: p.name, hint: p.code })),
                ]} />

              {RANGED.has(reportType) && (
                <>
                  <TextField label="From" type="date" value={from} onChange={setFrom} hint="Leave empty for no start date." />
                  <TextField label="To" type="date" value={to} onChange={setTo} hint="Leave empty for today." />
                </>
              )}

              {HORIZONED.has(reportType) && (
                <SelectField
                  label="Expiring within" value={withinDays} onChange={setWithinDays}
                >
                  <option value="30">30 days</option>
                  <option value="90">90 days</option>
                  <option value="180">180 days</option>
                  <option value="365">A year</option>
                </SelectField>
              )}
            </div>
          )}

          {canExport && (
            // Held at the foot of the screen on a phone: the button was
            // below ten cards, out of sight of the choice it acts on.
            <div className="flex flex-wrap items-center justify-end gap-3 max-md:sticky max-md:bottom-0 max-md:z-10 max-md:-mx-5 max-md:border-t max-md:border-neutral-200 max-md:bg-white max-md:px-5 max-md:py-3">
              {!reportType && <span className="text-small text-neutral-500">Choose a report first.</span>}
              <Button
                loading={busy}
                disabled={!reportType}
                icon={<FileSpreadsheet className="h-4 w-4" />}
                onClick={request}
              >
                {selected ? `Generate ${selected.label} (CSV)` : 'Generate CSV'}
              </Button>
            </div>
          )}
        </CardBody>
      </Card>

      <section>
        <div className="mb-2 flex items-baseline justify-between">
          <div className="flex items-center gap-1">
            <h2 className="text-h4 text-neutral-900">Your exports</h2>
            {/* The 24 hours stays on screen — it decides when to download;
                only the reason is behind ⓘ. */}
            <span className="text-small text-neutral-500">· deleted after 24 hours</span>
            <InfoTip label="Why exports are deleted">
              Files are deleted after 24 hours because a spreadsheet of employee data has no access
              control of its own once it leaves this system.
            </InfoTip>
          </div>
          {pending && (
            <span role="status" className="text-small text-neutral-500">Generating — this updates itself.</span>
          )}
        </div>

        <DataTable<ReportJobRow>
          rows={rows}
          loading={jobs.loading}
          error={jobs.error}
          onRetry={jobs.reload}
          rowKey={(j) => j.id}
          empty={{
            title: 'No exports yet',
            hint: canExport
              ? 'Choose a report above. Yours appear here — a colleague’s do not.'
              : 'Exports you request appear here. Your role cannot generate them.',
          }}
          columns={[
            {
              key: 'report', header: 'Report', alwaysVisible: true,
              cell: (j) => (
                <div>
                  <div className="font-medium text-neutral-900">
                    {definitions.find((d) => d.key === j.reportType)?.label
                      ?? j.reportType.toLowerCase().replace(/_/g, ' ')}
                  </div>
                  <div className="text-caption text-neutral-500">
                    Requested {formatDateTime(j.createdAt)}
                  </div>
                </div>
              ),
            },
            {
              key: 'status', header: 'Status',
              cell: (j) => (
                <div>
                  <StatusBadge tone={statusTone(j.status)}>
                    {STATUS_LABEL[j.status]}
                  </StatusBadge>
                  {j.status === 'FAILED' && j.lastError && (
                    <div className="mt-1 max-w-xs text-xs text-overdue-600">{j.lastError}</div>
                  )}
                  {j.status === 'EXPIRED' && (
                    <div className="mt-1 text-xs text-neutral-500">
                      File deleted. Request it again.
                    </div>
                  )}
                </div>
              ),
            },
            {
              key: 'rows', header: 'Rows', align: 'right',
              cell: (j) => (
                <span className="tabular-nums text-neutral-700">
                  {j.rowCount === null ? '—' : j.rowCount.toLocaleString()}
                </span>
              ),
            },
            {
              key: 'size', header: 'Size', align: 'right',
              cell: (j) => (
                <span className="tabular-nums text-small text-neutral-500">{size(j.byteSize)}</span>
              ),
            },
            {
              key: 'download', header: 'Actions', align: 'right', alwaysVisible: true, mobileLabel: '',
              cell: (j) => (j.status === 'READY' ? (
                <PermissionGate has={permissions} need="report.export">
                  <RowActions>
                    <IconAction
                      label={`Download ${definitions.find((d) => d.key === j.reportType)?.label
                        ?? j.reportType.toLowerCase().replace(/_/g, ' ')} (CSV)`}
                      icon={Download}
                      loading={downloading === j.id}
                      onClick={() => download(j.id)}
                    />
                  </RowActions>
                </PermissionGate>
              ) : null),
            },
          ]}
        />
      </section>
    </div>
  );
}
